import { useMemo } from "react";

import { type VariantProps, cva } from "class-variance-authority";

import { cn } from "@lib/utils";

import { Label } from "./Label";

const fieldVariants = cva("data-[invalid=true]:text-destructive group/field flex w-full gap-1", {
	variants: {
		orientation: {
			vertical: "flex-col *:w-full [&>.sr-only]:w-auto",
			horizontal: `flex-row items-center has-[>[data-slot=field-content]]:items-start
			*:data-[slot=field-label]:flex-auto has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px`,
			responsive: `flex-col *:w-full @md/field-group:flex-row @md/field-group:items-center @md/field-group:*:w-auto
			@md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:*:data-[slot=field-label]:flex-auto
			[&>.sr-only]:w-auto @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px`,
		},
	},
	defaultVariants: {
		orientation: "vertical",
	},
});

/** Combine labels, controls, and help text to compose accessible form fields and grouped inputs.
 * @prop className - The class name to apply to the field.
 * @prop orientation - The orientation of the field.
 */
const Field = ({
	className,
	orientation = "vertical",
	...props
}: React.ComponentProps<"div"> & VariantProps<typeof fieldVariants>) => (
	<div
		role="group"
		data-slot="field"
		data-orientation={orientation}
		className={cn(fieldVariants({ orientation }), className)}
		{...props}
	/>
);

const FieldLabel = ({ className, ...props }: React.ComponentProps<typeof Label>) => (
	<Label
		data-slot="field-label"
		className={cn(
			`group/field-label peer/field-label flex w-fit gap-2 leading-snug group-data-[disabled=true]/field:opacity-50
			has-data-checked:border-primary/30 has-data-checked:bg-primary/5 has-[>[data-slot=field]]:rounded-lg
			has-[>[data-slot=field]]:border *:data-[slot=field]:p-2.5 dark:has-data-checked:border-primary/20
			dark:has-data-checked:bg-primary/10`,
			"has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col",
			className,
		)}
		{...props}
	/>
);

const FieldError = ({
	className,
	children,
	errors,
	...props
}: React.ComponentProps<"div"> & {
	errors?: Array<{ message?: string } | undefined>;
}) => {
	const content = useMemo(() => {
		if (children) {
			return children;
		}

		if (!errors?.length) {
			return null;
		}

		const uniqueErrors = [...new Map(errors.map((error) => [error?.message, error])).values()];

		if (uniqueErrors?.length === 1) {
			return uniqueErrors[0]?.message;
		}

		return (
			<ul className="ml-4 flex list-disc flex-col gap-1">
				{uniqueErrors.map((error, index) => error?.message && <li key={index}>{error.message}</li>)}
			</ul>
		);
	}, [children, errors]);

	if (!content) {
		return null;
	}

	return (
		<div
			role="alert"
			data-slot="field-error"
			className={cn("text-sm font-normal text-error-foreground", className)}
			{...props}
		>
			{content}
		</div>
	);
};

FieldError.displayName = "FieldError";
FieldLabel.displayName = "FieldLabel";
Field.displayName = "Field";

export { Field, FieldLabel, FieldError };
